import { CheckCircleOutline } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { toast } from 'react-toastify'
import { requestProvider } from 'src/api/requestProvider'
import { scheduledTaskEndpoints } from 'src/api/scheduledTask'
import { invalidateTaskCache, taskEndpoints, useGetFilterCalendarScheduleTask } from 'src/api/tasks'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { IScheduledTask } from 'src/generated-types/scheduledTask'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useDebouncedSearchInput } from 'src/hooks/useDebouncedSearchInput'
import { useRowCount } from 'src/hooks/useViewportHeight'
import { MUIThemeColors } from 'src/mui-theme'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { onTasksDateRangeChange } from '../Tasks.action'
import ScheduledTaskEditForm from './ScheduledTaskEditForm'
import TaskCalendar from './TaskCalendar'
import TaskResources from './TaskResources'
import TasksFilter from './TasksFilter'
import {
  buildTaskFilterQueryParams,
  mapScheduledTasksToCalendarEvents,
  mergeSearchIntoQueryParams,
  TaskFilterDateRange,
  TaskFilterQueryParams,
} from './WorkOrderOccurrencesTab.action'

interface WorkOrderOccurrencesTabProps {
  customerId: EntityId
  isCalendar: boolean
}

const WorkOrderOccurrencesTab: React.FC<WorkOrderOccurrencesTabProps> = ({ customerId, isCalendar }) => {
  const { t } = useTranslation(['task', 'common'])
  const theme = useTheme()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const [queryParams, setQueryParams] = useState<TaskFilterQueryParams>({})
  const [searchInput, setSearchInput] = useState('')
  const [filterFormValues, setFilterFormValues] = useState<TaskFilterDateRange>({})
  const [prevIsCalendar, setPrevIsCalendar] = useState(isCalendar)
  const calendarQueryRange = useRef<TaskFilterDateRange>({})
  const filterDatesRef = useRef<TaskFilterDateRange>({})
  const rowCount = useRowCount(35)
  const debouncedSearch = useDebouncedSearchInput(searchInput, 400)

  const tableQueryParams = useMemo(
    () => mergeSearchIntoQueryParams(queryParams, debouncedSearch),
    [queryParams, debouncedSearch],
  )

  const { data: tasksData } = useGetFilterCalendarScheduleTask(customerId as number, tableQueryParams, {
    enabled: isCalendar,
  })

  const showResources = useCallback(
    (row: IScheduledTask) => {
      showDialog(
        TaskResources,
        {
          componentProps: {
            taskResources: row?.taskResources,
            customerId,
            taskId: row?.tasks?.id,
            localityId: row?.tasks?.localityId,
            task: row?.tasks,
            scheduleTaskId: row?.id,
            scheduleTaskStartDate: row?.startDate,
            scheduleTaskEndDate: row?.endDate,
          },
          opts: { maxWidth: 'md', fullWidth: true },
          title: t('resources'),
        },
        undefined,
        true,
      )
    },
    [customerId, showDialog, t],
  )

  const showScheduledResources = useCallback(
    row => {
      showDialog(
        TaskResources,
        {
          componentProps: {
            taskResources: row?.task?.taskResources,
            customerId,
            taskId: row?.task?.id,
            localityId: row?.locality?.id,
            task: row?.task,
            scheduleTaskId: row?.id,
            scheduleTaskStartDate: row?.startDate,
            scheduleTaskEndDate: row?.endDate,
          },
          opts: { maxWidth: 'md', fullWidth: true },
          title: t('resources'),
        },
        undefined,
        true,
      )
    },
    [customerId, showDialog, t],
  )

  const editOccurrence = useCallback(
    (row: IScheduledTask) => {
      showDialog(ScheduledTaskEditForm, {
        componentProps: { initialValues: row, customerId },
        opts: { maxWidth: 'sm', fullWidth: true },
        title: t('edit-task'),
      })
    },
    [customerId, showDialog, t],
  )

  const deleteOccurrence = useCallback(
    (row: IScheduledTask) => {
      showConfirmDialog(
        t('delete-occurrence', { ns: 'task' }),
        t('delete-occurrence-message', { ns: 'task' }),
        { acceptText: t('yes', { ns: 'common' }), cancelText: t('no', { ns: 'common' }) },
        async pop => {
          try {
            await requestProvider.delete(
              scheduledTaskEndpoints.singleScheduledTask(row.tasks?.localityId as EntityId, row.id),
            )
            invalidateTaskCache.getCustomerAdminScheduleTaskByFilter(customerId)
            invalidateTaskCache.useGetFilterCalendarScheduleTask(customerId)
            toast.success(t('occurrence-deleted', { ns: 'task' }))
          } catch {
            toast.error(t('updatingError', { ns: 'common' }))
          }
          pop()
        },
      )
    },
    [customerId, showConfirmDialog, t],
  )

  const events = useMemo(() => mapScheduledTasksToCalendarEvents(tasksData), [tasksData])

  const columns: GridColumns = useMemo(
    () => [
      { field: 'id', headerName: t('id', { ns: 'common' }), minWidth: 80, maxWidth: 100, flex: 0 },
      {
        field: 'title',
        headerName: t('title', { ns: 'common' }),
        flex: 1,
        cellClassName: 'font-weight-semi-bold',
        renderCell: ({ row }) => row.tasks?.title ?? '-',
      },
      {
        field: 'locality',
        headerName: t('locality', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => row.tasks?.locality?.name ?? '-',
      },
      { field: 'description', headerName: t('completion-note', { ns: 'task' }), flex: 1 },
      {
        field: 'startDate',
        headerName: t('startDate', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => getFormattedDate(row.startDate),
      },
      {
        field: 'endDate',
        headerName: t('endDate', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => getFormattedDate(row.endDate),
      },
      {
        field: 'signedById',
        headerName: t('status', { ns: 'common' }),
        flex: 0.8,
        renderCell: ({ row }) =>
          row.signedById ? (
            <CheckCircleOutline sx={{ color: getThemeColor(theme, MUIThemeColors.successMain) }} />
          ) : (
            t('open', { ns: 'common' })
          ),
      },
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 60,
        getActions: ({ row }) =>
          row.signedById
            ? []
            : [
              <GridActionsCellItem
                key={`edit-${row.id}`}
                label={t('edit', { ns: 'common' })}
                icon={<MdEdit size={24} />}
                onClick={() => editOccurrence(row as IScheduledTask)}
                color="primary"
                showInMenu
              />,
              <GridActionsCellItem
                key={`delete-${row.id}`}
                label={t('delete-occurrence', { ns: 'task' })}
                icon={<MdDelete size={24} style={{ color: getThemeColor(theme, MUIThemeColors.errorMain) }} />}
                onClick={() => deleteOccurrence(row as IScheduledTask)}
                showInMenu
              />,
            ],
      },
    ],
    [deleteOccurrence, editOccurrence, t, theme],
  )

  const dateRangeChange = useCallback(date => {
    const { startDate: filterStartDate, endDate: filterEndDate } = onTasksDateRangeChange(date)
    calendarQueryRange.current = { startDate: filterStartDate, endDate: filterEndDate }
    setQueryParams(p => ({ ...p, ...{ startDate: filterStartDate, endDate: filterEndDate } }))
  }, [])

  if (isCalendar !== prevIsCalendar) {
    setPrevIsCalendar(isCalendar)
    const { startDate, endDate } = filterDatesRef.current
    if (isCalendar) dateRangeChange(startDate ?? new Date())
    else setQueryParams(p => ({ ...p, ...{ startDate, endDate } }))
  }

  const taskFilterChange = useCallback(
    params => {
      const {
        queryParams: nextQueryParams,
        searchInput: nextSearchInput,
        filterDates,
      } = buildTaskFilterQueryParams(params, { isCalendar, calendarRange: calendarQueryRange.current })
      setQueryParams(nextQueryParams)
      setSearchInput(nextSearchInput)
      filterDatesRef.current = filterDates
      setFilterFormValues(filterDates)
    },
    [isCalendar],
  )

  return (
    <>
      <TasksFilter
        onChange={taskFilterChange}
        isDisabled={isCalendar}
        key="filter"
        startDate={filterFormValues.startDate}
        endDate={filterFormValues.endDate}
      />
      <Box padding={'8px'}>
        {isCalendar ? (
          <TaskCalendar
            onClick={showScheduledResources}
            events={events}
            onMonthChange={date => dateRangeChange(date)}
            defaultDate={filterFormValues.startDate ?? undefined}
          />
        ) : (
          <InfiniteScrollTable<IScheduledTask>
            id={'work-order-occurrences'}
            queryUrl={taskEndpoints.filterScheduleTask(customerId)}
            queryParams={tableQueryParams}
            columns={columns}
            onRowClick={({ row }) => showResources(row as IScheduledTask)}
            pageSize={rowCount}
            autoHeight={false}
            height={window.innerHeight - 380 + 'px'}
            getRowHeight={() => 35}
          />
        )}
      </Box>
    </>
  )
}

export default WorkOrderOccurrencesTab
